import React, { useEffect, useState } from 'react'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsTooltip } from './HlsTooltip'

/** PiP icon — Bitmovin picinpic1.svg (32x32 viewBox, large screen with small inset window). */
const PipIcon: React.FC = () => (
  <svg width={24} height={24} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      fill="#fff"
      d="M5 8.3h19.8v5.5H27V8.3c0-1.2-1-2.2-2.2-2.2H5c-1.2 0-2.2 1-2.2 2.2v12.1c0 1.2 1 2.2 2.2 2.2h7.7v-2.2H5V8.3zM14.9 16v9.9h14.3V16H14.9z"
    />
    <polygon fill="#fff" points="12.7,10.5 7.2,10.5 7.2,16 9.4,16 9.4,12.7 12.7,12.7" />
  </svg>
)

/**
 * Picture-in-Picture toggle button for the control bar.
 * Only renders when the browser supports the Picture-in-Picture API.
 */
export const HlsPipButton: React.FC<{ videoRef: React.RefObject<HTMLVideoElement | null> }> = ({ videoRef }) => {
  const { controller } = useHlsPlayerUI()
  const { t } = useTranslate('common')
  const [pipActive, setPipActive] = useState(false)
  const [pipSupported, setPipSupported] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    setPipSupported(!!video && 'requestPictureInPicture' in video)
  }, [videoRef])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onEnter = () => setPipActive(true)
    const onLeave = () => setPipActive(false)

    video.addEventListener('enterpictureinpicture', onEnter)
    video.addEventListener('leavepictureinpicture', onLeave)
    return () => {
      video.removeEventListener('enterpictureinpicture', onEnter)
      video.removeEventListener('leavepictureinpicture', onLeave)
    }
  }, [videoRef])

  const handleClick = async () => {
    const video = videoRef.current
    if (!video || !controller) return

    try {
      if (pipActive) {
        await document.exitPictureInPicture()
      } else {
        await video.requestPictureInPicture()
      }
    } catch {
      // PiP request can fail if user gesture requirement isn't met or feature is disabled
    }
  }

  if (!pipSupported) return null

  return (
    <button
      type="button"
      className="group relative flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20"
      onClick={handleClick}
      aria-label={t('pictureInPicture', 'Picture-in-Picture')}
    >
      <PipIcon />
      <HlsTooltip
        title={t('pictureInPicture', 'Picture-in-Picture')}
        subtitle={t('pictureInPictureDescription', 'Watch in a floating window.')}
      />
    </button>
  )
}
